import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Html5Qrcode } from 'html5-qrcode'
import { useGymId } from '@structural-climbing/auth'
import { Button, Card, CardBody, Spinner } from '@structural-climbing/ui'
import { useCheckIn } from './useCheckIn'
import { parseScanPayload } from './parseScanPayload'
import { safePause, safeResume, safeStop, scannerStateName } from './qrScannerControl'
import { describePayload, reportScan } from './scanTelemetry'
import { useWedgeScanner, type WedgeReport } from './useWedgeScanner'
import { NavIcon } from '../../layouts/NavIcon'

/**
 * The camera list, for the Sentry report when `start()` fails. A front-desk PC
 * with no webcam and one whose permission was denied fail the same way
 * otherwise.
 */
async function describeCameras(): Promise<string[] | string> {
  try {
    if (typeof Html5Qrcode.getCameras !== 'function') return 'unavailable'
    const cameras = await Html5Qrcode.getCameras()
    return cameras.map((c) => c.label || c.id)
  } catch (error) {
    return error instanceof Error ? `error: ${error.name}` : 'error'
  }
}

// --- QR Scanner tab ---

// Nobody has to be at the desk: a result clears itself and the scanner takes
// the next member. A refusal stays longer, the member has something to read.
const AUTO_RESET_SUCCESS_MS = 3000
const AUTO_RESET_NOTICE_MS = 5000
// A phone still held up to the camera when it resumes is the same scan, not a
// second one. Without this it flips to "already checked in" for as long as the
// member stands there.
const DUPLICATE_SCAN_WINDOW_MS = 10000

/**
 * Reads member codes from a USB scanner (always) and the camera (when started),
 * checks the member in, shows the result and gets ready for the next one.
 *
 * `compact` is the Check-in page's Members and History tabs: no camera, nothing
 * on screen while idle, and a result appears as a banner above the list. The
 * Camera tab, and the phone's full-screen scanner, use the full layout.
 */
export function QrScanner({
  debug,
  autoStartCamera = false,
  compact = false,
  source = 'qr-tab',
}: {
  debug: boolean
  /** Opened from a phone's scan button: the camera is the scanner, so start it. */
  autoStartCamera?: boolean
  compact?: boolean
  /** Where the USB listener is mounted, for the Sentry report. */
  source?: string
}) {
  const gymId = useGymId()
  const { t } = useTranslation()
  const { checkIn, stateFor, resultFor, errorFor, reset } = useCheckIn()

  const scannerRef = useRef<Html5Qrcode | null>(null)
  const scannerDivId = 'qr-scanner-region'

  type QrState = 'idle' | 'scanning' | 'loading' | 'success' | 'already_in' | 'error'
  const [qrState, setQrState] = useState<QrState>(autoStartCamera ? 'scanning' : 'idle')
  const [cameraEnabled, setCameraEnabled] = useState(autoStartCamera)
  const [scanned, setScanned] = useState<{ userId: string; childId?: string } | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const [lastReport, setLastReport] = useState<ScanDiagnostics | null>(null)
  // Only a result that came from a scan clears itself. A camera that would not
  // start is for the staff to read, so it waits for a click.
  const [autoReset, setAutoReset] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const lastCameraScanRef = useRef<{ payload: string; at: number } | null>(null)

  const resolvedState = scanned ? stateFor(scanned.userId, scanned.childId) : 'idle'
  const resolvedResult = scanned ? resultFor(scanned.userId, scanned.childId) : null
  const resolvedError = scanned ? errorFor(scanned.userId, scanned.childId) : null

  useEffect(() => {
    if (!scanned) return
    const s = resolvedState
    if (s === 'loading') setQrState('loading')
    else if (s === 'success') setQrState('success')
    else if (s === 'already_in') setQrState('already_in')
    else if (s === 'error') setQrState('error')
  }, [resolvedState, scanned])

  // Report the outcome of the check-in call itself, so a scan that decoded
  // perfectly but was refused by the API is distinguishable in Sentry from one
  // that never decoded at all.
  useEffect(() => {
    if (!scanned || resolvedState === 'idle' || resolvedState === 'loading') return
    reportScan(`check-in ${resolvedState}`, {
      stage: 'check-in',
      level: resolvedState === 'error' ? 'warning' : 'info',
      data: {
        result: resolvedState,
        has_child: Boolean(scanned.childId),
        error: resolvedError,
        entries_remaining: resolvedResult?.entries_remaining ?? null,
      },
    })
  }, [resolvedState, resolvedError, resolvedResult, scanned])

  const handleScan = useCallback(
    async (payload: string, wedge?: WedgeReport) => {
      safePause(scannerRef.current)
      setLocalError(null)
      // A scan may land while the previous result is still on screen, so drop
      // that result first instead of letting the new one inherit it.
      if (scanned) reset(scanned.userId, scanned.childId)
      setScanned(null)
      setAutoReset(true)

      const parsed = parseScanPayload(payload)
      const shared = {
        source: wedge ? 'hardware-scanner' : 'camera',
        format: parsed.format,
        parts: parsed.parts,
        trimmed: parsed.trimmed,
        wedge: wedge ?? null,
      }

      if (!parsed.ok) {
        // The payload is already garbage, so logging it verbatim identifies
        // nobody and is the only way to see what the scanner actually sent.
        reportScan('scan payload did not parse', {
          stage: 'parse',
          level: 'warning',
          data: { ...shared, reason: parsed.reason, payload: describePayload(payload, true) },
        })
        setLastReport({ outcome: 'invalid', payload, wedge: wedge ?? null })
        setLocalError(t('scan.invalidQr'))
        setQrState('error')
        return
      }

      if (parsed.gymId !== gymId) {
        reportScan('scan payload was for another gym', {
          stage: 'parse',
          level: 'warning',
          data: {
            ...shared,
            scanned_gym_id: parsed.gymId,
            expected_gym_id: gymId,
            payload: describePayload(payload, true),
          },
        })
        setLastReport({ outcome: 'wrong-gym', payload, wedge: wedge ?? null })
        setLocalError(t('scan.wrongGym'))
        setQrState('error')
        return
      }

      reportScan('scan payload parsed', {
        stage: 'parse',
        level: 'info',
        data: { ...shared, has_child: Boolean(parsed.childId), payload: describePayload(payload) },
      })
      setLastReport({ outcome: 'parsed', payload, wedge: wedge ?? null })

      setScanned({ userId: parsed.userId, childId: parsed.childId })
      await checkIn(parsed.userId, parsed.childId)
    },
    [gymId, checkIn, t, scanned, reset],
  )

  const handleScanRef = useRef(handleScan)
  const handleResetRef = useRef(handleReset)
  const qrStateRef = useRef(qrState)

  useEffect(() => {
    handleScanRef.current = handleScan
    handleResetRef.current = handleReset
    qrStateRef.current = qrState
  })

  const showingResult = qrState === 'success' || qrState === 'already_in' || qrState === 'error'

  useEffect(() => {
    if (!autoReset || !showingResult) return

    let remaining = (qrState === 'success' ? AUTO_RESET_SUCCESS_MS : AUTO_RESET_NOTICE_MS) / 1000
    setSecondsLeft(remaining)
    const timer = setInterval(() => {
      remaining -= 1
      if (remaining > 0) {
        setSecondsLeft(remaining)
        return
      }
      clearInterval(timer)
      handleResetRef.current()
    }, 1000)

    return () => clearInterval(timer)
  }, [autoReset, showingResult, qrState])

  // Only `cameraEnabled` belongs in the dependency list. Depending on
  // `handleScan` restarted the camera every time `checkIn` or `t` changed
  // identity, and every restart is a chance to stop a scanner that is still
  // starting up.
  useEffect(() => {
    if (!cameraEnabled) return

    if (!window.isSecureContext) {
      reportScan('camera blocked, the page is not a secure context', {
        stage: 'camera',
        level: 'warning',
        data: { origin: window.location.origin },
      })
      setLocalError(t('scan.cameraHttps'))
      setAutoReset(false)
      setQrState('error')
      setCameraEnabled(false)
      return
    }

    const scanner = new Html5Qrcode(scannerDivId)
    scannerRef.current = scanner
    reportScan('camera starting', { stage: 'camera', level: 'info' })

    scanner
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          const now = Date.now()
          const last = lastCameraScanRef.current
          lastCameraScanRef.current = { payload: decodedText, at: now }
          if (last?.payload === decodedText && now - last.at < DUPLICATE_SCAN_WINDOW_MS) return

          reportScan('camera decoded a code', {
            stage: 'camera',
            level: 'info',
            data: { payload: describePayload(decodedText) },
          })
          void handleScanRef.current(decodedText)
        },
        () => {},
      )
      .then(() => {
        reportScan('camera started', {
          stage: 'camera',
          level: 'info',
          data: { state: scannerStateName(scanner) },
        })
      })
      .catch((error: unknown) => {
        // The old code swallowed this. `NotAllowedError` (permission),
        // `NotFoundError` (a front-desk PC with no webcam) and
        // `NotReadableError` (another app holds the camera) all showed the same
        // sentence and left nothing behind to tell them apart.
        void describeCameras().then((cameras) => {
          reportScan('camera failed to start', {
            stage: 'camera',
            level: 'warning',
            error,
            data: { state: scannerStateName(scanner), cameras },
          })
        })
        setLocalError(t('scan.cameraDenied'))
        setAutoReset(false)
        setQrState('error')
        setCameraEnabled(false)
      })

    return () => {
      void safeStop(scanner)
    }
  }, [cameraEnabled, t])

  // Leaving the Camera tab for a list tab turns the camera off; the USB
  // scanner keeps listening.
  useEffect(() => {
    if (!compact || !cameraEnabled) return
    setCameraEnabled(false)
    setQrState((s) => (s === 'scanning' ? 'idle' : s))
  }, [compact, cameraEnabled])

  useWedgeScanner({
    mode: 'active',
    source,
    onPayload: (payload, report) => void handleScanRef.current(payload, report),
    // A result on screen is not a reason to turn the next member away.
    canAccept: () => qrStateRef.current !== 'loading',
  })

  function handleStartCamera() {
    setQrState('scanning')
    setCameraEnabled(true)
  }

  function handleReset() {
    if (scanned) reset(scanned.userId, scanned.childId)
    setScanned(null)
    setLocalError(null)
    setAutoReset(false)
    if (cameraEnabled) {
      setQrState('scanning')
      safeResume(scannerRef.current)
    } else {
      setQrState('idle')
    }
  }

  const displayError = localError ?? resolvedError
  const countdown = autoReset && (
    <p className="mt-2 text-center text-xs text-foreground/50">
      {t('scan.nextScanIn', { seconds: secondsLeft })}
    </p>
  )

  if (compact) {
    return (
      <>
        <ScanResultBanner
          state={qrState}
          name={resolvedResult?.member_name ?? ''}
          entriesRemaining={resolvedResult?.entries_remaining ?? null}
          error={displayError}
          onDismiss={handleReset}
        />
        {debug && <ScanDiagnosticsPanel report={lastReport} />}
      </>
    )
  }

  return (
    <div className="flex flex-col items-center gap-6">
      <div id={scannerDivId} className={qrState === 'scanning' ? 'w-full max-w-sm' : 'hidden'} />

      {qrState === 'idle' && (
        <div className="flex w-full max-w-sm flex-col items-center gap-4 py-6">
          <p className="text-center text-sm text-foreground/60">{t('scan.scannerReady')}</p>
          <Button
            variant="bordered"
            className="border-1"
            startContent={<NavIcon name="camera" size={16} />}
            onClick={handleStartCamera}
          >
            {t('scan.startCamera')}
          </Button>
        </div>
      )}

      {qrState === 'loading' && (
        <Card className="w-full max-w-sm">
          <CardBody className="flex items-center justify-center gap-3 py-10">
            <Spinner size="lg" color="primary" />
            <p className="text-sm text-foreground/60">{t('scan.checkingIn')}</p>
          </CardBody>
        </Card>
      )}

      {qrState === 'success' && resolvedResult && (
        <Card className="w-full max-w-sm border-success/30">
          <CardBody className="py-6">
            <div className="mb-4 flex items-center gap-2">
              <span className="text-2xl">✓</span>
              <p className="text-lg font-semibold text-success">{t('scan.checkedIn')}</p>
            </div>
            <p className="text-base font-medium">{resolvedResult.member_name}</p>
            {resolvedResult.entries_remaining !== null && (
              <p className="mt-1 text-sm text-foreground/60">
                {t('scan.entriesRemaining', { count: resolvedResult.entries_remaining })}
              </p>
            )}
            <Button className="mt-5 w-full" color="primary" onClick={handleReset}>
              {t('scan.scanNext')}
            </Button>
            {countdown}
          </CardBody>
        </Card>
      )}

      {qrState === 'already_in' && (
        <Card className="w-full max-w-sm border-warning/30">
          <CardBody className="py-6">
            <div className="mb-4 flex items-center gap-2">
              <span className="text-2xl">⚠</span>
              <p className="text-lg font-semibold text-warning">{t('scan.alreadyCheckedIn')}</p>
            </div>
            <p className="text-base font-medium">{resolvedResult?.member_name ?? ''}</p>
            <p className="mt-1 text-sm text-foreground/60">{t('scan.alreadyInGym')}</p>
            <Button className="mt-5 w-full" variant="flat" onClick={handleReset}>
              {t('scan.scanNext')}
            </Button>
            {countdown}
          </CardBody>
        </Card>
      )}

      {qrState === 'error' && (
        <Card className="w-full max-w-sm border-danger/30">
          <CardBody className="py-6">
            <div className="mb-4 flex items-center gap-2">
              <span className="text-2xl">✗</span>
              <p className="text-lg font-semibold text-danger">{t('scan.failed')}</p>
            </div>
            <p className="text-sm text-foreground/60">{displayError}</p>
            <Button className="mt-5 w-full" variant="flat" onClick={handleReset}>
              {t('scan.tryAgain')}
            </Button>
            {countdown}
          </CardBody>
        </Card>
      )}

      {debug && <ScanDiagnosticsPanel report={lastReport} />}
    </div>
  )
}

// --- Diagnostics panel (only while ?scanDebug=1 is on) ---

interface ScanDiagnostics {
  outcome: 'parsed' | 'invalid' | 'wrong-gym'
  payload: string
  wedge: WedgeReport | null
}

function ScanDiagnosticsPanel({ report }: { report: ScanDiagnostics | null }) {
  const { t } = useTranslation()

  const rows: [string, string][] = report
    ? [
        ['outcome', report.outcome],
        ['payload', JSON.stringify(report.payload)],
        ['length', String(report.payload.length)],
        ['source', report.wedge ? 'hardware scanner' : 'camera'],
        ...(report.wedge
          ? ([
              ['terminator', report.wedge.terminator],
              ['key events', String(report.wedge.key_events)],
              ['buffer resets', String(report.wedge.resets)],
              ['discarded chars', String(report.wedge.discarded_chars)],
              [
                'gaps ms (min/avg/max)',
                `${report.wedge.gap_min_ms ?? '-'}/${report.wedge.gap_avg_ms ?? '-'}/${report.wedge.gap_max_ms ?? '-'} (limit ${report.wedge.gap_threshold_ms})`,
              ],
              ['duration ms', String(report.wedge.duration_ms)],
              ['codes', report.wedge.codes.join(' ')],
              ['keys', report.wedge.keys.join('')],
              ['dropped keys', report.wedge.dropped.join(' ') || '-'],
              ['modifiers', report.wedge.modifiers.join(' ') || '-'],
              ['focused element', report.wedge.active_element],
            ] as [string, string][])
          : []),
      ]
    : []

  return (
    <Card className="w-full max-w-2xl border-warning/40">
      <CardBody className="gap-2 py-4">
        <p className="text-sm font-semibold text-warning">{t('scan.diagnosticsTitle')}</p>
        {report ? (
          <dl className="grid grid-cols-[minmax(0,10rem)_1fr] gap-x-3 gap-y-1 font-mono text-xs">
            {rows.map(([label, value]) => (
              <Fragment key={label}>
                <dt className="text-foreground/50">{label}</dt>
                <dd className="break-all">{value}</dd>
              </Fragment>
            ))}
          </dl>
        ) : (
          <p className="text-xs text-foreground/60">{t('scan.diagnosticsWaiting')}</p>
        )}
      </CardBody>
    </Card>
  )
}

// --- History tab ---

const BANNER_TONE = {
  loading: { bar: 'bg-default-300', icon: '' },
  success: { bar: 'bg-success', icon: '✓' },
  already_in: { bar: 'bg-warning', icon: '!' },
  error: { bar: 'bg-danger', icon: '✗' },
} as const

/** A scan's result as one line above the list, while the USB scanner keeps listening. */
function ScanResultBanner({
  state,
  name,
  entriesRemaining,
  error,
  onDismiss,
}: {
  state: string
  name: string
  entriesRemaining: number | null
  error: string | null
  onDismiss: () => void
}) {
  const { t } = useTranslation()
  if (!(state in BANNER_TONE)) return null
  const tone = BANNER_TONE[state as keyof typeof BANNER_TONE]
  const title =
    state === 'loading'
      ? t('scan.checkingIn')
      : state === 'success'
        ? t('scan.bannerIn', { name })
        : state === 'already_in'
          ? t('scan.bannerAlreadyIn', { name })
          : t('scan.failed')
  const detail =
    state === 'success'
      ? entriesRemaining !== null
        ? t('scan.entriesRemaining', { count: entriesRemaining })
        : null
      : state === 'already_in'
        ? t('scan.alreadyInGym')
        : state === 'error'
          ? error
          : null
  return (
    <Card shadow="sm" className="mb-4 overflow-hidden" role="status">
      <CardBody className="flex flex-row items-center gap-3 py-3 pl-5">
        <span className={`absolute inset-y-0 left-0 w-1 ${tone.bar}`} aria-hidden="true" />
        {state === 'loading' ? (
          <Spinner size="sm" />
        ) : (
          <span
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${tone.bar}`}
            aria-hidden="true"
          >
            {tone.icon}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{title}</p>
          {detail && <p className="text-sm text-foreground/60">{detail}</p>}
        </div>
        {state !== 'loading' && (
          <Button size="sm" variant="light" onPress={onDismiss}>
            {t('common.close')}
          </Button>
        )}
      </CardBody>
    </Card>
  )
}
